import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Plug, Trash2, Building2, Wifi, CheckCircle, RefreshCw, ExternalLink, ChevronDown } from 'lucide-react';
import { useGetIntegrations, useUpsertIntegration, useDeleteIntegration, getGetIntegrationsQueryKey } from '@edison/api-client-react';
import { customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { DevicesTab } from './devices-tab.js';
import { useAuthStore } from '@/store/auth-store.js';

type TestResult = { ok: boolean; error?: string; detail?: string } | null;

interface ConfigField {
  key: string;
  label: string;
  placeholder: string;
  hint?: string;
}

const PROVIDER_SCHEMA: Record<string, { label: string; description: string; fields: ConfigField[] }> = {
  anthropic: {
    label: 'Anthropic',
    description: 'Connects to the Anthropic API to pull Claude token usage for LLM cost allocations.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        placeholder: 'sk-ant-…',
        hint: 'Requires usage:read scope. This key is stored encrypted and never returned via API.',
      },
    ],
  },
  aws: {
    label: 'AWS Cost Explorer',
    description: 'Pulls all AWS spend from Cost Explorer for your account. Requires a dedicated IAM user with read-only billing access.',
    fields: [
      {
        key: 'access_key_id',
        label: 'AWS Access Key ID',
        placeholder: 'AKIAIOSFODNN7EXAMPLE',
        hint: 'IAM user must have billing:GetCostAndUsage permission. Stored encrypted, never returned via API.',
      },
      {
        key: 'secret_access_key',
        label: 'AWS Secret Access Key',
        placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
        hint: 'Stored encrypted, never returned via API.',
      },
    ],
  },
  gcp: {
    label: 'Google Cloud Platform',
    description: 'Pulls GCP spend per project via BigQuery billing export. Costs are auto-matched to Edison projects by GCP project name.',
    fields: [
      {
        key: 'service_account_json',
        label: 'Service account JSON',
        placeholder: '{"type":"service_account","project_id":"..."}',
        hint: 'Service account must have BigQuery Data Viewer + BigQuery Job User roles on the project that owns the billing export dataset. Stored encrypted, never returned via API.',
      },
      {
        key: 'bigquery_project_id',
        label: 'BigQuery project ID',
        placeholder: 'e.g. my-company-billing',
        hint: 'The GCP project that owns the BigQuery billing export dataset. Find this in GCP Console → Billing → Billing export.',
      },
      {
        key: 'bigquery_dataset',
        label: 'BigQuery dataset name',
        placeholder: 'e.g. billing_export',
        hint: 'The dataset name you chose when setting up billing export. The table inside will be auto-detected.',
      },
      {
        key: 'bigquery_table_prefix',
        label: 'BigQuery table prefix (optional)',
        placeholder: 'e.g. gcp_billing_export_resource_v1_*',
        hint: 'Leave blank to use the standard export (gcp_billing_export_v1_*). Set to gcp_billing_export_resource_v1_* if you enabled the detailed resource-level billing export in GCP.',
      },
    ],
  },
  openai: {
    label: 'OpenAI',
    description: 'Pulls OpenAI spend per project using the admin API. Costs are auto-matched to Edison projects by OpenAI project name.',
    fields: [
      {
        key: 'admin_api_key',
        label: 'Admin API key',
        placeholder: 'sk-admin-…',
        hint: 'Requires an organisation admin key with usage read access. Stored encrypted, never returned via API.',
      },
    ],
  },
  replit: {
    label: 'Replit',
    description: 'Connects to the Replit Teams API to track R&D spend on Replit-hosted workloads.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        placeholder: 'replit_api_…',
        hint: 'A Replit API key with Teams read access. Stored encrypted, never returned via API.',
      },
    ],
  },
};

// ─── Xero integration section ─────────────────────────────────────────────────

interface XeroStatus {
  connected: boolean;
  hasCredentials: boolean;
  connectedAt: string | null;
}

interface PayrollSnapshot {
  id: string;
  period: string;
  grossWagesAud: string;
  accountsIncluded: string | null;
  source: string;
  syncedAt: string;
}

function XeroSection() {
  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [snapshots, setSnapshots] = useState<PayrollSnapshot[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncResult, setSyncResult] = useState<{ created: number; updated: number; lineCount: number; leaveBalanceRows: number; leaveBalanceError: string | null } | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [vendors, setVendors] = useState<string[]>([]);
  const [accountNames, setAccountNames] = useState<string[]>([]);
  const [mapDraft, setMapDraft] = useState<Record<string, string>>({});
  const [savingMap, setSavingMap] = useState(false);
  const [mapSaved, setMapSaved] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await customInstance<{ data: XeroStatus }>({ url: '/api/payroll/xero/status', method: 'GET' });
      setStatus(res.data);
    } catch {
      setStatus({ connected: false, hasCredentials: false, connectedAt: null });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchSnapshots = useCallback(async () => {
    setLoadingSnapshots(true);
    try {
      const now = new Date();
      const fyEnd = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
      const res = await customInstance<{ data: { items: PayrollSnapshot[] } }>({
        url: `/api/payroll/snapshots?incomeYear=FY${fyEnd}`,
        method: 'GET',
      });
      setSnapshots(res.data.items);
    } catch {
      // non-fatal
    } finally {
      setLoadingSnapshots(false);
    }
  }, []);

  // Fetches vendors, Xero account names, and the stored map in parallel. Each is
  // wrapped independently so a single failure (e.g. sim mode, no admin access) does
  // not abort the others.
  const fetchMapping = useCallback(async () => {
    const [vendorsRes, accountsRes, mapRes] = await Promise.all([
      customInstance<{ data: { vendors: string[] } }>({ url: '/api/cost-allocations/vendors', method: 'GET' })
        .catch(() => ({ data: { vendors: [] } })),
      customInstance<{ data: { accounts: string[] } }>({ url: '/api/payroll/xero/accounts', method: 'GET' })
        .catch(() => ({ data: { accounts: [] } })),
      customInstance<{ data: { map: Record<string, string> } }>({ url: '/api/payroll/xero/account-map', method: 'GET' })
        .catch(() => ({ data: { map: {} } })),
    ]);
    setVendors(vendorsRes.data.vendors);
    setAccountNames(accountsRes.data.accounts);
    setMapDraft(mapRes.data.map);
  }, []);

  useEffect(() => {
    void fetchStatus();
    void fetchMapping();
  }, [fetchStatus, fetchMapping]);

  // Handle OAuth redirect-back query params
  // wouter's useLocation returns pathname only — use window.location.search for query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroParam = params.get('xero');
    if (xeroParam === 'connected') {
      void fetchStatus();
      void fetchSnapshots();
    }
  }, [fetchStatus, fetchSnapshots]);

  const handleSaveKey = async (key: string) => {
    const value = formValues[key];
    if (!value?.trim()) return;
    setSavingKey(key);
    try {
      await customInstance({
        url: '/api/integrations',
        method: 'PUT',
        data: { provider: 'xero', configKey: key, configValue: value.trim() },
      });
      setFormValues((prev) => { const next = { ...prev }; delete next[key]; return next; });
      await fetchStatus();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Save failed';
      setSyncError(msg);
    } finally {
      setSavingKey(null);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await customInstance<{ data: { authUrl: string } }>({
        url: '/api/payroll/xero/start-oauth',
        method: 'POST',
        data: {},
      });
      window.location.href = res.data.authUrl;
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Failed to start Xero connection';
      setSyncError(msg);
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      // Sync the previous AND current FY (Jul→Jun each) as two calls — Xero's monthly P&L
      // report caps at 12 columns per request, so a single 24-month range would silently
      // drop the first year. The previous FY is what an in-flight claim reads.
      const now = new Date();
      const fyEnd = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
      type SyncData = { created: number; updated: number; lineCount: number; leaveBalanceRows: number; leaveBalanceError: string | null };
      const totals: SyncData = { created: 0, updated: 0, lineCount: 0, leaveBalanceRows: 0, leaveBalanceError: null };
      for (const end of [fyEnd - 1, fyEnd]) {
        const res = await customInstance<{ data: SyncData }>({
          url: '/api/payroll/xero/sync',
          method: 'POST',
          data: { periodStart: `${end - 1}-07-01`, periodEnd: `${end}-06-30` },
        });
        totals.created += res.data.created;
        totals.updated += res.data.updated;
        totals.lineCount += res.data.lineCount;
        totals.leaveBalanceRows += res.data.leaveBalanceRows ?? 0;
        totals.leaveBalanceError = totals.leaveBalanceError ?? res.data.leaveBalanceError ?? null;
      }
      setSyncResult(totals);
      await fetchSnapshots();
      // Newly-seen Xero accounts should appear in the mapping datalist immediately.
      await fetchMapping();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Sync failed';
      setSyncError(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveMap = async () => {
    setSavingMap(true);
    setMapError(null);
    setMapSaved(false);
    try {
      // Only persist vendors with a non-empty mapping.
      const cleanedMap = Object.fromEntries(
        Object.entries(mapDraft).filter(([, terms]) => terms.trim() !== ''),
      );
      await customInstance({
        url: '/api/integrations',
        method: 'PUT',
        data: { provider: 'xero', configKey: 'gl_account_map', configValue: JSON.stringify(cleanedMap) },
      });
      setMapSaved(true);
      setTimeout(() => setMapSaved(false), 2000);
      await fetchMapping();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Save failed';
      setMapError(msg);
    } finally {
      setSavingMap(false);
    }
  };

  if (loadingStatus) {
    return <div className="rounded-lg border p-5 h-48 skeleton" />;
  }

  const xeroFields = [
    { key: 'client_id', label: 'Client ID', placeholder: 'From Xero developer app', hint: 'Found in Xero Developer Portal → My Apps → your app → OAuth 2.0 credentials.' },
    { key: 'client_secret', label: 'Client Secret', placeholder: '••••••••••••', hint: 'Keep this secret — stored encrypted, never returned via API.' },
  ];

  return (
    <div className="rounded-lg border p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Plug className="h-5 w-5 text-muted-foreground mt-1 shrink-0" aria-hidden="true" />
          <div>
            <h2 className="font-medium text-sm">Xero Payroll</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Pulls employee wages from your Xero P&amp;L month-by-month. Used to overlay R&amp;D labour costs against total payroll for integrity checking.
            </p>
          </div>
        </div>
        {status?.connected && (
          <div className="flex items-center gap-1 text-emerald-700 text-xs shrink-0">
            <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Connected
          </div>
        )}
      </div>

      {/* Credential fields */}
      <div className="space-y-3">
        {xeroFields.map((field) => {
          const isSaving = savingKey === field.key;
          return (
            <div key={field.key}>
              <label className="text-xs font-medium block mb-1" htmlFor={`xero-${field.key}`}>
                {field.label}
              </label>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <input
                    id={`xero-${field.key}`}
                    type={field.key === 'client_secret' ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={formValues[field.key] ?? ''}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full rounded border bg-background px-3 py-2 text-xs font-mono"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{field.hint}</p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSaveKey(field.key)}
                  disabled={isSaving || !formValues[field.key]?.trim()}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          );
        })}

        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="xero-account_filter">
            Wage account filter
          </label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <input
                id="xero-account_filter"
                type="text"
                placeholder="Wages,Salaries"
                value={formValues['account_filter'] ?? ''}
                onChange={(e) => setFormValues((prev) => ({ ...prev, account_filter: e.target.value }))}
                className="w-full rounded border bg-background px-3 py-2 text-xs font-mono"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated terms. Any P&amp;L account name containing one of these terms is included. Default: <code>Wages,Salaries</code>
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSaveKey('account_filter')}
              disabled={savingKey === 'account_filter' || !formValues['account_filter']?.trim()}
            >
              {savingKey === 'account_filter' ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Xero setup instructions */}
      {!status?.hasCredentials && (
        <div className="rounded border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">Setup steps:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Go to <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">Xero Developer Portal <ExternalLink className="h-3 w-3" /></a></li>
            <li>Create a new app → Web App type</li>
            <li>Add redirect URI: <code className="bg-muted px-1 rounded select-all">https://z6z3mgwne5.execute-api.ap-southeast-2.amazonaws.com/api/payroll/xero/callback</code></li>
            <li>Copy Client ID and Client Secret, paste above and save</li>
            <li>Click Connect Xero to authorise</li>
          </ol>
        </div>
      )}

      {/* OAuth connect button */}
      {status?.hasCredentials && !status.connected && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handleConnect()}
          disabled={connecting}
        >
          {connecting ? 'Redirecting to Xero…' : 'Connect Xero'}
        </Button>
      )}

      {/* Reconnect + sync */}
      {status?.connected && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSync()}
            disabled={syncing}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {syncing ? 'Syncing payroll…' : 'Sync Payroll (FY)'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleConnect()}
            disabled={connecting}
          >
            {connecting ? 'Redirecting…' : 'Reconnect'}
          </Button>
        </div>
      )}

      {syncResult && (
        <p className="text-xs text-emerald-700">
          ✓ Sync complete — {syncResult.lineCount} months pulled, {syncResult.created} new, {syncResult.updated} updated
          {syncResult.leaveBalanceRows > 0 && <> · {syncResult.leaveBalanceRows} leave-balance months (balance sheet)</>}
          {syncResult.leaveBalanceRows === 0 && !syncResult.leaveBalanceError && <> · no leave-liability accounts found on the balance sheet</>}
        </p>
      )}
      {syncResult?.leaveBalanceError && (
        <p className="text-xs text-amber-700">
          ⚠ Wages synced, but leave balances failed: {syncResult.leaveBalanceError.includes('401') ? 'Xero authorisation lacks balance-sheet access — click Reconnect above to re-consent, then sync again.' : syncResult.leaveBalanceError}
        </p>
      )}

      {syncError && (
        <p className="text-xs text-destructive">✗ {syncError}</p>
      )}

      {/* Payroll snapshot table */}
      {status?.connected && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Monthly wages (current FY)</p>
            {loadingSnapshots && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
            {!loadingSnapshots && snapshots.length === 0 && (
              <button className="text-xs text-primary underline" onClick={() => void fetchSnapshots()}>Load</button>
            )}
          </div>
          {snapshots.length > 0 && (
            <div className="rounded border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Month</th>
                    <th className="text-right px-3 py-2 font-medium">Gross Wages (AUD)</th>
                    <th className="text-left px-3 py-2 font-medium">Accounts</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => {
                    const accounts: string[] = s.accountsIncluded ? JSON.parse(s.accountsIncluded) as string[] : [];
                    return (
                      <tr key={s.id} className="border-t">
                        <td className="px-3 py-2 font-mono">{s.period}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          ${parseFloat(s.grossWagesAud).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{accounts.join(', ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* GL account mapping */}
      <div className="space-y-2 pt-2 border-t">
        <div>
          <p className="text-xs font-medium">GL account mapping</p>
          <p className="text-xs text-muted-foreground mt-1">
            Map each provider (vendor) to the Xero account name(s) its spend lands in. Comma-separated terms, case-insensitive substring match — same as the wage account filter.
          </p>
        </div>

        {vendors.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No vendors on cost allocations yet. Add costs (with a vendor) or run a provider sync first.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {vendors.map((vendor) => (
                <div key={vendor} className="flex items-center gap-2">
                  <label
                    className="text-xs w-28 shrink-0 truncate"
                    htmlFor={`xero-map-${vendor}`}
                    title={vendor}
                  >
                    {vendor}
                  </label>
                  <input
                    id={`xero-map-${vendor}`}
                    list="xero-account-names"
                    type="text"
                    placeholder="e.g. Amazon Web Services"
                    value={mapDraft[vendor] ?? ''}
                    onChange={(e) =>
                      setMapDraft((prev) => ({ ...prev, [vendor]: e.target.value }))
                    }
                    className="flex-1 rounded border bg-background px-3 py-2 text-xs font-mono"
                    autoComplete="off"
                  />
                </div>
              ))}
            </div>
            <datalist id="xero-account-names">
              {accountNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            {accountNames.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Sync Xero to populate account name suggestions.
              </p>
            )}
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSaveMap()}
                disabled={savingMap}
              >
                {savingMap ? 'Saving…' : 'Save mapping'}
              </Button>
              {mapSaved && <span className="text-xs text-emerald-700">Saved</span>}
              {mapError && <span className="text-xs text-destructive">✗ {mapError}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Org profile ──────────────────────────────────────────────────────────────

interface OrgProfile {
  legalName: string;
  abn: string;
  address?: string;
  industry?: string;
  companySize?: string;
  annualTurnover?: string;
  description?: string;
}

function OrgProfileSection() {
  const [profile, setProfile] = useState<OrgProfile>({
    legalName: '', abn: '', address: '', industry: '', companySize: '', annualTurnover: '', description: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void customInstance<{ data: OrgProfile }>({ url: '/api/grants/org-profile', method: 'GET' })
      .then((res) => setProfile(res.data))
      .catch(() => { /* 404 expected — no profile yet */ })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await customInstance<{ data: OrgProfile }>({
        url: '/api/grants/org-profile', method: 'PUT', data: profile,
      });
      setProfile(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-48 rounded-lg border skeleton" />;

  const fieldCls = 'w-full rounded border bg-background px-3 py-2 text-sm';

  return (
    <div className="rounded-lg border p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Building2 className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
        <div>
          <h2 className="font-medium text-sm">Organisation Profile</h2>
          <p className="text-xs text-muted-foreground">Used by the Grant Application Aid to pre-fill answers about your company.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-name">Legal name *</label>
          <input id="org-name" className={fieldCls} value={profile.legalName} onChange={(e) => setProfile((p) => ({ ...p, legalName: e.target.value }))} placeholder="Acme Pty Ltd" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-abn">ABN *</label>
          <input id="org-abn" className={fieldCls} value={profile.abn} onChange={(e) => setProfile((p) => ({ ...p, abn: e.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="12 345 678 901" maxLength={11} />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-address">Address</label>
          <input id="org-address" className={fieldCls} value={profile.address ?? ''} onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))} placeholder="Suite 1, 123 Innovation St, Sydney NSW 2000" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-industry">Industry</label>
          <input id="org-industry" className={fieldCls} value={profile.industry ?? ''} onChange={(e) => setProfile((p) => ({ ...p, industry: e.target.value }))} placeholder="Software / SaaS" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-size">Company size</label>
          <select id="org-size" className={fieldCls} value={profile.companySize ?? ''} onChange={(e) => setProfile((p) => ({ ...p, companySize: e.target.value }))}>
            <option value="">Select…</option>
            <option value="sole_trader">Sole trader</option>
            <option value="2_10">2–10</option>
            <option value="11_50">11–50</option>
            <option value="51_200">51–200</option>
            <option value="200_plus">200+</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" htmlFor="org-turnover">Annual turnover</label>
          <select id="org-turnover" className={fieldCls} value={profile.annualTurnover ?? ''} onChange={(e) => setProfile((p) => ({ ...p, annualTurnover: e.target.value }))}>
            <option value="">Select…</option>
            <option value="under_1m">Under $1M</option>
            <option value="1m_5m">$1M–$5M</option>
            <option value="5m_20m">$5M–$20M</option>
            <option value="over_20m">Over $20M</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium block mb-1" htmlFor="org-desc">Company description</label>
          <textarea id="org-desc" className={`${fieldCls} min-h-[80px]`} rows={3} value={profile.description ?? ''} onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))} placeholder="One paragraph about what your company does, used to contextualise grant applications." />
        </div>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={() => void handleSave()} disabled={saving || !profile.legalName || !profile.abn}>
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save Organisation'}
        </Button>
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const { user } = useAuthStore();
  const isDeveloper = user?.role === 'developer';
  const { data, isLoading, error, refetch } = useGetIntegrations({ query: { enabled: !isDeveloper, queryKey: getGetIntegrationsQueryKey() } });
  const { mutate: upsert } = useUpsertIntegration();
  const { mutate: remove, isPending: isRemoving } = useDeleteIntegration();

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [testingProviders, setTestingProviders] = useState<Set<string>>(new Set());
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());

  const toggleProvider = (provider: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  };

  const configured = data?.data?.items ?? [];
  const configuredSet = new Set(configured.map((c) => `${c.provider}:${c.configKey}`));

  const allFieldsConfigured = (provider: string): boolean => {
    const schema = PROVIDER_SCHEMA[provider];
    if (!schema) return false;
    return schema.fields.every((f) => configuredSet.has(`${provider}:${f.key}`));
  };

  const runTest = async (provider: string) => {
    setTestingProviders((prev) => new Set([...prev, provider]));
    setTestResults((prev) => ({ ...prev, [provider]: null }));
    try {
      const r = await customInstance<{ data: { results: Record<string, { configured: boolean; ok: boolean; error?: string; detail?: string }> } }>({
        url: '/api/expenditure/diagnostics',
        method: 'GET',
      });
      const result = r.data.results[provider];
      setTestResults((prev) => ({ ...prev, [provider]: result ?? { ok: false, error: 'No result returned' } }));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Test failed';
      setTestResults((prev) => ({ ...prev, [provider]: { ok: false, error: msg } }));
    } finally {
      setTestingProviders((prev) => {
        const next = new Set(prev);
        next.delete(provider);
        return next;
      });
    }
  };

  const handleSave = (provider: string, key: string) => {
    const value = formValues[`${provider}:${key}`];
    if (!value?.trim()) return;
    setSavingKey(`${provider}:${key}`);
    upsert(
      { data: { provider: provider as 'anthropic' | 'aws' | 'gcp' | 'openai' | 'replit', configKey: key, configValue: value.trim() } },
      {
        onSuccess: async () => {
          setFormValues((prev) => {
            const next = { ...prev };
            delete next[`${provider}:${key}`];
            return next;
          });
          setSavingKey(null);
          await refetch();
          // Auto-test after the last field for this provider is saved
          const schema = PROVIDER_SCHEMA[provider];
          if (schema) {
            const updatedConfigured = new Set([...(data?.data?.items ?? []).map((c) => `${c.provider}:${c.configKey}`), `${provider}:${key}`]);
            const allDone = schema.fields.every((f) => updatedConfigured.has(`${provider}:${f.key}`));
            if (allDone) void runTest(provider);
          }
        },
        onError: () => setSavingKey(null),
      }
    );
  };

  const handleRemove = (provider: string, key: string) => {
    remove(
      { provider, configKey: key },
      { onSuccess: () => {
        void refetch();
        setTestResults((prev) => ({ ...prev, [provider]: null }));
      }},
    );
  };

  if (!isDeveloper && isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-32 w-full rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  if (!isDeveloper && error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load integrations.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isDeveloper
            ? 'Register your device and set up Claude Code hooks so your work is captured for R&D evidence.'
            : 'Connect third-party services to enable automatic cost allocation syncing. Credentials are stored encrypted and never returned via the API.'}
        </p>
      </div>

      {!isDeveloper && <OrgProfileSection />}

      {!isDeveloper && <XeroSection />}

      {!isDeveloper && <div className="space-y-2">
        {Object.entries(PROVIDER_SCHEMA).map(([provider, schema]) => {
          const isConfigured = allFieldsConfigured(provider);
          const isExpanded = expandedProviders.has(provider);

          return (
            <div key={provider} className="rounded-lg border border-hairline overflow-hidden bg-card">
              <button
                onClick={() => toggleProvider(provider)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-3 transition-colors"
                aria-expanded={isExpanded}
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full shrink-0 transition-colors ${isConfigured ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                  aria-label={isConfigured ? 'Configured' : 'Not configured'}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-ink">{schema.label}</p>
                  <p className="text-[11.5px] text-dim truncate">{schema.description}</p>
                </div>
                {isConfigured && isExpanded && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); void runTest(provider); }}
                    disabled={testingProviders.has(provider)}
                    aria-label={`Test ${schema.label} connection`}
                  >
                    <Wifi className={`h-3.5 w-3.5 ${testingProviders.has(provider) ? 'animate-pulse' : ''}`} aria-hidden="true" />
                    {testingProviders.has(provider) ? 'Testing…' : 'Test'}
                  </Button>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-dim shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {isExpanded && (
                <div className="border-t border-hairline px-4 py-4 space-y-3 bg-background">
                  {testResults[provider] !== null && testResults[provider] !== undefined && !testingProviders.has(provider) && (
                    testResults[provider]!.ok ? (
                      <p className="text-xs text-emerald-700">✓ {testResults[provider]!.detail ?? 'Connected'}</p>
                    ) : (
                      <p className="text-xs text-destructive">✗ {testResults[provider]!.error ?? 'Connection failed'}</p>
                    )
                  )}

                  {schema.fields.map((field) => {
                    const compositeKey = `${provider}:${field.key}`;
                    const isFieldConfigured = configuredSet.has(compositeKey);
                    const isSaving = savingKey === compositeKey;
                    const configEntry = configured.find((c) => c.provider === provider && c.configKey === field.key);

                    return (
                      <div key={field.key}>
                        <label className="text-xs font-medium text-foreground block mb-1" htmlFor={compositeKey}>
                          {field.label}
                        </label>
                        {isFieldConfigured ? (
                          <div className="flex items-center gap-3">
                            <div className="flex-1 rounded border bg-muted/50 px-3 py-2 text-xs text-muted-foreground font-mono">
                              ••••••••
                              <span className="ml-2 not-italic text-muted-foreground/60">
                                Updated {new Date(configEntry!.updatedAt).toLocaleDateString('en-AU')}
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemove(provider, field.key)}
                              disabled={isRemoving}
                              aria-label={`Remove ${field.label}`}
                              className="text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                            <button
                              onClick={() => setFormValues((prev) => ({ ...prev, [compositeKey]: '' }))}
                              className="text-xs text-primary underline hover:no-underline"
                            >
                              Replace
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              {field.key === 'service_account_json' ? (
                                <textarea
                                  id={compositeKey}
                                  placeholder={field.placeholder}
                                  value={formValues[compositeKey] ?? ''}
                                  onChange={(e) =>
                                    setFormValues((prev) => ({ ...prev, [compositeKey]: e.target.value }))
                                  }
                                  rows={6}
                                  className="w-full rounded border bg-background px-3 py-2 text-xs font-mono resize-y"
                                  autoComplete="off"
                                  spellCheck={false}
                                />
                              ) : (
                                <input
                                  id={compositeKey}
                                  type={(field.key === 'api_key' || field.key === 'admin_api_key' || field.key === 'secret_access_key') ? 'password' : 'text'}
                                  placeholder={field.placeholder}
                                  value={formValues[compositeKey] ?? ''}
                                  onChange={(e) =>
                                    setFormValues((prev) => ({ ...prev, [compositeKey]: e.target.value }))
                                  }
                                  className="w-full rounded border bg-background px-3 py-2 text-xs font-mono"
                                  autoComplete="off"
                                />
                              )}
                              {field.hint && (
                                <p className="text-xs text-muted-foreground mt-1">{field.hint}</p>
                              )}
                            </div>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSave(provider, field.key)}
                              disabled={isSaving || !formValues[compositeKey]?.trim()}
                            >
                              {isSaving ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>}

      <div className="rounded-lg border border-hairline overflow-hidden bg-card">
        <button
          onClick={() => toggleProvider('devices')}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-3 transition-colors"
          aria-expanded={expandedProviders.has('devices')}
          data-tour="devices-section"
        >
          <span className="inline-block h-2 w-2 rounded-full shrink-0 bg-emerald-500" aria-label="Active" />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-ink">Claude Code Devices</p>
            <p className="text-[11.5px] text-dim truncate">Register your development machines to capture Claude Code activity as R&amp;D evidence.</p>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-dim shrink-0 transition-transform ${expandedProviders.has('devices') ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
        {expandedProviders.has('devices') && (
          <div className="border-t border-hairline px-4 py-4 bg-background">
            <DevicesTab />
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Admin access required to manage integrations. All changes are logged in the audit trail.
      </p>
    </div>
  );
}
